import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ServiceArea } from './mapLocationPickerTypes';

interface MapLocationPickerProps {
  areas: ServiceArea[];
  selectedId?: string | number;
  onSelect: (id: string | number) => void;
}

const areaSubtitle = (area: ServiceArea): string => {
  const parts = [area.city, area.state].filter(Boolean);
  const sub = parts.join(', ');
  if (!sub || sub === area.address1) {
    return '';
  }
  return sub;
};

const isDark = (theme: Theme): boolean => theme.palette.mode === 'dark';
const pickerShadow = (theme: Theme): string =>
  isDark(theme) ? '0 10px 28px rgba(0,0,0,0.35)' : '0 8px 24px rgba(0,0,0,0.12)';
const menuShadow = (theme: Theme): string =>
  isDark(theme) ? '0 16px 36px rgba(0,0,0,0.45)' : '0 12px 32px rgba(0,0,0,0.16)';
const pickerBg = (theme: Theme): string =>
  alpha(theme.palette.background.paper, 0.92);
const pickerBgOpen = (theme: Theme): string =>
  alpha(theme.palette.background.paper, 0.98);
const menuBg = (theme: Theme): string =>
  alpha(theme.palette.background.paper, 0.96);

export const MapLocationPicker = ({
  areas,
  selectedId,
  onSelect,
}: MapLocationPickerProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = areas.find((area) => area.id === selectedId) || areas[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointer = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (!areas.length || !selected) {
    return null;
  }

  return (
    <Box
      ref={rootRef}
      sx={{
        pointerEvents: 'auto',
        position: 'relative',
        width: 'min(320px, calc(100vw - 48px))',
      }}
    >
      <Box
        component="button"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          minHeight: 48,
          width: '100%',
          cursor: 'pointer',
          alignItems: 'center',
          gap: '10px',
          borderRadius: '14px',
          border: 1,
          borderColor: 'divider',
          bgcolor: open ? pickerBgOpen : pickerBg,
          px: 1.5,
          py: 1,
          textAlign: 'left',
          font: 'inherit',
          color: 'text.primary',
          boxShadow: pickerShadow,
          backdropFilter: 'blur(16px)',
          transition:
            'border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease',
          '&:hover': {
            borderColor: 'rgb(var(--muted))',
            bgcolor: pickerBgOpen,
          },
          '&:hover > .MuiSvgIcon-root:first-of-type': { color: 'primary.main' },
        }}
      >
        <LocationOnOutlinedIcon
          sx={{
            flexShrink: 0,
            fontSize: 20,
            color: open ? 'primary.main' : 'rgb(var(--muted))',
          }}
        />
        <Box
          component="span"
          sx={{
            display: 'flex',
            minWidth: 0,
            flex: 1,
            flexDirection: 'column',
            gap: '1px',
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.7px',
              color: 'rgb(var(--muted))',
            }}
          >
            {t('map.serviceArea')}
          </Box>
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.3,
              color: 'text.primary',
            }}
          >
            {selected.address1 || t('map.selectArea')}
          </Box>
        </Box>
        <KeyboardArrowDownRoundedIcon
          sx={{
            flexShrink: 0,
            fontSize: 22,
            color: 'rgb(var(--muted))',
            transition: 'transform 150ms ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </Box>
      {open ? (
        <Box
          role="listbox"
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 'calc(100% + 8px)',
            maxHeight: 280,
            overflowY: 'auto',
            borderRadius: '14px',
            border: 1,
            borderColor: 'divider',
            bgcolor: menuBg,
            p: '6px',
            boxShadow: menuShadow,
            backdropFilter: 'blur(16px)',
            transformOrigin: 'top',
            animation: 'capabilities-panel-in 0.22s ease-out',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          {areas.map((area) => {
            const isSelected = area.id === selected.id;
            const sub = areaSubtitle(area);
            return (
              <Box
                key={area.id}
                component="button"
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onSelect(area.id);
                  setOpen(false);
                }}
                sx={{
                  display: 'flex',
                  minHeight: 44,
                  width: '100%',
                  cursor: 'pointer',
                  alignItems: 'center',
                  gap: '10px',
                  borderRadius: '10px',
                  border: 'none',
                  bgcolor: isSelected ? 'rgb(var(--line))' : 'transparent',
                  px: '10px',
                  py: 1,
                  textAlign: 'left',
                  font: 'inherit',
                  color: isSelected ? 'text.primary' : 'rgb(var(--muted))',
                  '&:hover': {
                    bgcolor: 'rgb(var(--field))',
                    color: 'text.primary',
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    minWidth: 0,
                    flex: 1,
                    flexDirection: 'column',
                    gap: '2px',
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}
                  >
                    {area.address1}
                  </Box>
                  {sub ? (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11,
                        lineHeight: 1.3,
                        color: 'rgb(var(--muted))',
                      }}
                    >
                      {sub}
                    </Box>
                  ) : null}
                </Box>
                {isSelected ? (
                  <CheckRoundedIcon
                    sx={{ flexShrink: 0, fontSize: 18, color: 'primary.main' }}
                  />
                ) : null}
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};
